import { storeToRefs } from 'pinia'
import { useNotificationStore } from '@/legacy/store/modules/notification'
import { DialOptions } from '@/legacy/types/entities/phoneNumbers'
import { NotificationType } from '@/legacy/types/notifications'
import { useFlagStore } from '@/store/flags/flags'
import { isSoftphoneSessionExpired } from '@/tokens/amazonCCP/sessionMonitoring'
import { amazonConnectStore } from '@/tokens/amazonCCP/store'
import { isRestrictedDialNumber } from './restrictedDialNumbers'
import { useRestrictedDialStore } from './restrictedDialStore'

export const NO_SOFTPHONE_WINDOW_MESSAGE =
  'No Call Control Panel window is open. Open the "Call Control Panel" application in your dock, then try the call again.'
export const EXPIRED_SOFTPHONE_SESSION_MESSAGE =
  'The Call Control Panel session has expired. Reopen the "Call Control Panel" application in your dock, then try the call again.'

/**
 * Why this call must not be placed through the dedicated softphone window,
 * or null if it can be.
 *
 * The softphone popup owns the audio session, so a call dialled while it is
 * missing or dead reaches the member's phone with nobody able to speak to
 * them. Better to refuse the click than to put a silent call on their line.
 *
 * Deliberately fails *open*: unless we can affirmatively establish that no
 * usable window exists, the call goes through. Blocking an agent out of
 * their phone on a bad status read would be worse than the problem. See
 * CT-821.
 */
async function softphoneWindowBlockReason(): Promise<string | null> {
  if (isSoftphoneSessionExpired()) {
    return EXPIRED_SOFTPHONE_SESSION_MESSAGE
  }

  // Presence is detected through a Web Lock; with no Web Locks API there is
  // no way to know, so don't guess.
  if (!navigator.locks) {
    return null
  }

  try {
    if (await amazonConnectStore().isSoftphoneWindowOpen()) {
      return null
    }
    return NO_SOFTPHONE_WINDOW_MESSAGE
  } catch (err) {
    console.error('Could not determine softphone window status', err)
    return null
  }
}

/**
 * Give up the communication that is waiting for Amazon Connect call data.
 *
 * Callers hand a comm to the CCP store before dialing, and whichever Connect
 * contact turns up next claims it. So a dial that never reaches Connect has to
 * release the comm — otherwise the agent's next call, possibly to a different
 * member, gets its contact data written onto this one. See CT-821.
 */
export function releasePendingOutboundComm(): void {
  amazonConnectStore().setPendingOutboundCommId(null)
}

/**
 * Entry point for placing an outbound call.
 *
 * When the restricted-dial warning is enabled and the target number is on the
 * restricted list, this defers the call: it stashes the attempt and opens a
 * confirmation pop-up instead. Otherwise it dials immediately. The pop-up's
 * "Call anyway" action calls {@link performDial} directly to complete the call.
 * @param options Object with phone number and outbound details
 * @returns void
 */
export async function dial(options: DialOptions) {
  // @ts-ignore
  if (window.Cypress) {
    return
  }

  const { showRestrictedDialWarning } = storeToRefs(useFlagStore())
  if (
    showRestrictedDialWarning.value &&
    isRestrictedDialNumber(options.phoneNumber)
  ) {
    useRestrictedDialStore().requestConfirmation(options)
    return
  }

  await performDial(options)
}

/**
 * Place the outbound call without any restricted-number checks.
 *
 * Routes through Amazon Connect when enabled, otherwise falls back to the Zoom
 * phone link.
 * @param param0 Object with phone number inside
 * @param param0.phoneNumber
 * @param param0.outboundNumber
 * @param param0.outboundQueueArn
 * @returns void
 */
export async function performDial({
  phoneNumber,
  outboundNumber,
  outboundQueueArn,
}: DialOptions) {
  const { showAmazonCCP } = storeToRefs(useFlagStore())
  if (showAmazonCCP.value) {
    const blockReason = await softphoneWindowBlockReason()
    if (blockReason) {
      releasePendingOutboundComm()
      useNotificationStore().setNotification({
        message: blockReason,
        type: NotificationType.DANGER,
      })
      return
    }
    amazonConnectStore().startCall(phoneNumber, outboundQueueArn ?? undefined)
    return
  }

  // Zoom calls produce no Connect contact, so no call data is ever coming for
  // a comm handed over before this dial.
  releasePendingOutboundComm()

  console.debug('Opening call via zoom')

  const outboundNumberStr = outboundNumber
    ? `callerid=+${outboundNumber.countryCode ?? '1'}${
        outboundNumber.phoneNumber
      }`
    : ''

  window.open(`zoomphonecall://${phoneNumber}?${outboundNumberStr}`)
  return
}
